import React, { ReactNode, useRef, useState } from "react";

interface HolographicEffectsProps {
  children: ReactNode;
  className?: string;
  isHovered?: boolean;
  onMouseMove?: (position: { x: number; y: number }) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  disabled?: boolean;
}

const HolographicEffects: React.FC<HolographicEffectsProps> = ({
  children,
  className = "",
  isHovered: externalIsHovered,
  onMouseMove: externalOnMouseMove,
  onMouseEnter: externalOnMouseEnter,
  onMouseLeave: externalOnMouseLeave,
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [internalIsHovered, setInternalIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isHovered =
    externalIsHovered !== undefined ? externalIsHovered : internalIsHovered;
  const colorScheme = {
    primary: "rgba(255, 255, 255, 0.02)",
    secondary: "rgba(240, 248, 255, 0.03)",
    tertiary: "rgba(255, 255, 255, 0.04)",
    quaternary: "rgba(248, 250, 252, 0.025)",
    shadow: "from-gray-300/8 to-slate-300/8",
  };

  const multiplier = 1.5;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const position = { x, y };

    setMousePosition(position);
    externalOnMouseMove?.(position);
  };

  const handleMouseEnter = () => {
    setInternalIsHovered(true);
    externalOnMouseEnter?.();
  };

  const handleMouseLeave = () => {
    setInternalIsHovered(false);
    setMousePosition({ x: 0, y: 0 });
    externalOnMouseLeave?.();
  };

  const getReflectionAngle = () => {
    if (!containerRef.current || !isHovered) return 45;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateY = ((mousePosition.x - centerX) / centerX) * 5;
    const rotateX = ((mousePosition.y - centerY) / centerY) * -5;

    return 45 + rotateY * 2 + rotateX;
  };

  const getHolographicAngle = () => {
    if (!containerRef.current || !isHovered) return 45;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const relativeX = (mousePosition.x - centerX) / centerX;
    const relativeY = (mousePosition.y - centerY) / centerY;

    const mouseAngle = Math.atan2(relativeY, relativeX) * (180 / Math.PI);

    return mouseAngle + 45;
  };

  const reflectionAngle = getReflectionAngle();
  const holographicAngle = getHolographicAngle();
  const baseOpacity = isHovered ? 0.8 * multiplier : 0.4 * multiplier;

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={{ touchAction: "pan-y" }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative">{children}</div>

      <div
        className={`absolute inset-0 pointer-events-none rounded-xl overflow-hidden z-10`}
      >
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            opacity: isHovered ? 0.6 : 0,
            background: `linear-gradient(${reflectionAngle}deg,
                transparent 0%,
                ${colorScheme.primary} 35%,
                ${colorScheme.secondary} 45%,
                ${colorScheme.tertiary} 50%,
                ${colorScheme.secondary} 55%,
                ${colorScheme.primary} 65%,
                transparent 100%)`,
            transition: "background 0.1s ease-out, opacity 0.3s ease-out",
          }}
        />

        <div
          className="absolute inset-0 opacity-25"
          style={{
            background: `linear-gradient(135deg,
                transparent 0%,
                ${colorScheme.primary.replace("0.15", "0.08")} 25%,
                ${colorScheme.secondary.replace("0.25", "0.12")} 35%,
                ${colorScheme.tertiary.replace("0.35", "0.15")} 40%,
                ${colorScheme.secondary.replace("0.25", "0.12")} 45%,
                ${colorScheme.primary.replace("0.15", "0.08")} 55%,
                transparent 100%)`,
          }}
        />

        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(${holographicAngle}deg,
                ${colorScheme.primary.replace(
                  "0.15",
                  (0.1 * multiplier).toString()
                )} 0%,
                ${colorScheme.secondary.replace(
                  "0.25",
                  (0.15 * multiplier).toString()
                )} 20%,
                ${colorScheme.tertiary.replace(
                  "0.35",
                  (0.2 * multiplier).toString()
                )} 35%,
                ${colorScheme.quaternary} 50%,
                ${colorScheme.tertiary.replace(
                  "0.35",
                  (0.2 * multiplier).toString()
                )} 65%,
                ${colorScheme.secondary.replace(
                  "0.25",
                  (0.15 * multiplier).toString()
                )} 80%,
                ${colorScheme.primary.replace(
                  "0.15",
                  (0.1 * multiplier).toString()
                )} 100%),
              repeating-linear-gradient(${holographicAngle + 90}deg,
                transparent 0px,
                transparent 2px,
                rgba(255, 255, 255, ${0.03 * multiplier}) 2px,
                rgba(255, 255, 255, ${0.03 * multiplier}) 4px)`,
            opacity: baseOpacity,
            transition: "all 0.15s ease-out",
            mixBlendMode: "overlay",
          }}
        />

        <div
          className={`absolute inset-0 transition-all duration-300`}
          style={{
            borderRadius: "inherit",
            boxShadow: isHovered
              ? `0 0 ${20 * multiplier}px ${colorScheme.primary.replace(
                  "0.15",
                  (0.3 * multiplier).toString()
                )}, inset 0 0 ${
                  20 * multiplier
                }px ${colorScheme.secondary.replace(
                  "0.25",
                  (0.1 * multiplier).toString()
                )}`
              : "none",
          }}
        />
      </div>
    </div>
  );
};

export default HolographicEffects;
